import { AxiosError } from 'axios';
import { AutoConnectStatus, PaymentMethod, ConnectionsBillingPreviewPayload } from '../interfaces/paymentMethods';
import { Actions } from '../actions/paymentMethods';

import {
  VERIFY_SUFFICIENT_FUNDS_REQUEST,
  VERIFY_SUFFICIENT_FUNDS_SUCCESS,
  VERIFY_SUFFICIENT_FUNDS_FAILURE,
  FETCH_PAYMENT_METHODS_REQUEST,
  FETCH_PAYMENT_METHODS_SUCCESS,
  FETCH_PAYMENT_METHODS_FAILURE,
  FETCH_CONNECTIONS_BILLING_PREVIEW_REQUEST,
  FETCH_CONNECTIONS_BILLING_PREVIEW_SUCCESS,
  FETCH_CONNECTIONS_BILLING_PREVIEW_FAILURE,
  SET_DEFAULT_PAYMENT_METHOD_REQUEST,
  SET_DEFAULT_PAYMENT_METHOD_SUCCESS,
  SET_DEFAULT_PAYMENT_METHOD_FAILURE,
  GET_PAYMENT_METHODS_DETAILS_REQUEST,
  GET_PAYMENT_METHODS_DETAILS_SUCCESS,
  GET_PAYMENT_METHODS_DETAILS_FAILURE,
  RESET_NEW_PAYMENT_METHOD,
  RESET_CURRENT_SELECTED,
  SET_CURRENT_SELECTED
} from '../constants/paymentMethods';

type CurrentSelectedMethod = {
  id: string;
  isAuthorized: boolean | null;
};

export type State = {
  hasSufficientFunds: boolean;
  hasPaymentMethods: boolean | null;
  autoConnectStatus: AutoConnectStatus;
  pendingConnectionsBillingPreview: ConnectionsBillingPreviewPayload;
  paymentMethods: PaymentMethod[];
  currentSelected: CurrentSelectedMethod;
  newPaymentMethod: PaymentMethod;
  error: AxiosError | null;
  isFetching: boolean;
};

const initialState: State = {
  hasSufficientFunds: false,
  hasPaymentMethods: null,
  autoConnectStatus: AutoConnectStatus.NoInitialSelection,
  error: null,
  isFetching: false,
  pendingConnectionsBillingPreview: {
    taxes: 0,
    total: 0,
    connections: []
  },
  paymentMethods: [],
  currentSelected: {
    id: '',
    isAuthorized: false
  },
  newPaymentMethod: {
    name: '',
    id: '',
    type: '',
    cardType: '',
    isDefault: false,
    createdDate: '1970-01-01'
  }
};

export function reducer(state: State = initialState, action: Actions): State {
  switch (action.type) {
    case VERIFY_SUFFICIENT_FUNDS_REQUEST:
    case FETCH_PAYMENT_METHODS_REQUEST:
    case FETCH_CONNECTIONS_BILLING_PREVIEW_REQUEST:
    case SET_DEFAULT_PAYMENT_METHOD_REQUEST:
    case GET_PAYMENT_METHODS_DETAILS_REQUEST:
      return {
        ...state,
        isFetching: true
      };
    case VERIFY_SUFFICIENT_FUNDS_FAILURE:
    case FETCH_PAYMENT_METHODS_FAILURE:
    case GET_PAYMENT_METHODS_DETAILS_FAILURE:
    case SET_DEFAULT_PAYMENT_METHOD_FAILURE:
    case FETCH_CONNECTIONS_BILLING_PREVIEW_FAILURE:
      return {
        ...state,
        isFetching: false,
        error: action.error
      };
    case VERIFY_SUFFICIENT_FUNDS_SUCCESS:
      return {
        ...state,
        hasSufficientFunds: action.payload.hasSufficientFunds,
        currentSelected: {
          ...state.currentSelected,
          id: action.payload.methodId || state.currentSelected.id,
          isAuthorized: action.payload.hasSufficientFunds
        },
        isFetching: false
      };
    case FETCH_PAYMENT_METHODS_SUCCESS:
      const defaultMethod = action.payload.paymentMethods.find((method: { isDefault: boolean }) => method.isDefault);
      return {
        ...state,
        paymentMethods: action.payload.paymentMethods,
        hasPaymentMethods: !!defaultMethod,
        autoConnectStatus: action.payload.autoConnectStatus,
        isFetching: false,
        currentSelected: {
          ...state.currentSelected,
          id: defaultMethod?.id ?? ''
        }
      };
    case FETCH_CONNECTIONS_BILLING_PREVIEW_SUCCESS:
      return {
        ...state,
        pendingConnectionsBillingPreview: action.payload,
        isFetching: false
      };
    case SET_DEFAULT_PAYMENT_METHOD_SUCCESS:
      return {
        ...state,
        isFetching: false
      };
    case GET_PAYMENT_METHODS_DETAILS_SUCCESS:
      return {
        ...state,
        isFetching: false,
        newPaymentMethod: action.payload
      };
    case RESET_NEW_PAYMENT_METHOD:
      return {
        ...state,
        newPaymentMethod: {
          name: '',
          id: '',
          type: '',
          cardType: '',
          isDefault: false,
          createdDate: '1970-01-01'
        }
      };
    case RESET_CURRENT_SELECTED:
      return {
        ...state,
        currentSelected: {
          id: '',
          isAuthorized: false
        }
      };
    case SET_CURRENT_SELECTED:
      return {
        ...state,
        currentSelected: action.payload
      };
    default:
      return state;
  }
}
